import React from "react";
import { useEffect, useState } from "react";
import usePost from "../../hooks/usePost";
import { currencyList } from "../../data/currencyList";
import { paymentAccountList } from "../../data/paymentAccountList";
import SelectInput from "../ui/SelectInput";
import format from "date-fns/format";
import TextInput from "../ui/TextInput";
import PortalFinanceStageBasedCtas from "./PortalFinanceStageBasedCtas";
import LoadingOverlay from "../shared/LoadingOverlay";
import chevronIcon from "../../assets/icons/chevron-down-white.svg";
import { getTripTypeLabelFromLegCount } from "../../utilities/tripTypeDisplay";
import PortalDealDetailsEditToolbar from "./PortalDealDetailsEditToolbar";

const formatPriceForDisplay = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const num = Number(value);
    if (!Number.isFinite(num)) {
        return value;
    }
    return num.toLocaleString("en-GB");
};

const formatGbpForDisplay = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const num = Number(value);
    if (!Number.isFinite(num)) {
        return value;
    }
    return `£${num.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PortalFinanceDetails = ({ deal, legs }) => {
    const { sending, postData } = usePost();

    const [isEditMode, setIsEditMode] = useState(false);

    const [saleCurrency, setSaleCurrency] = useState();
    const [salePrice, setSalePrice] = useState();
    const [costCurrency, setCostCurrency] = useState();
    const [costPrice, setCostPrice] = useState();
    const [operatorChipping, setOperatorChipping] = useState();
    const [invoiceName, setInvoiceName] = useState("");
    const [paymentAccount, setPaymentAccount] = useState("");

    const [didSetDeal, setDidSetDeal] = useState(false);

    // Expandable leg sections
    const [expandedLegs, setExpandedLegs] = useState({});

    useEffect(() => {
        if (deal && !didSetDeal) {
            setCostCurrency(deal.cost_Currency);
            setCostPrice(deal.flight_Cost_Non_Currency);
            setSaleCurrency(deal.sale_Currency);
            setSalePrice(deal.sale_Price_Non_Currency);
            setOperatorChipping(deal.operator_chipping);
            setInvoiceName(deal.invoice_Display_Name);
            setPaymentAccount(deal.shY_Payment_Account);

            setDidSetDeal(true);
        }
    }, [deal, didSetDeal]);

    // Expand first leg only by default (match sales)
    useEffect(() => {
        if (legs && legs.length > 0) {
            const sorted = [...legs].sort((a, b) => (a.leg_Number < b.leg_Number ? -1 : 1));
            setExpandedLegs({ [sorted[0].id]: true });
        }
    }, [legs]);

    const updateDeal = () => {
        let formData = {};

        formData = {
            dealId: deal.id,
            Cost_Currency: costCurrency,
            Flight_Cost_Non_Currency: costPrice,
            Sale_Currency: saleCurrency,
            Sale_Price_Non_Currency: salePrice,
            Operator_chipping: operatorChipping,
            Invoice_Display_Name: invoiceName,
            SHY_Payment_Account: paymentAccount,
        };

        postData(formData, "/api/portal/update-deal");
    };

    const resetDealFieldsFromProps = () => {
        setCostCurrency(deal.cost_Currency);
        setCostPrice(deal.flight_Cost_Non_Currency);
        setSaleCurrency(deal.sale_Currency);
        setSalePrice(deal.sale_Price_Non_Currency);
        setOperatorChipping(deal.operator_chipping);
        setInvoiceName(deal.invoice_Display_Name);
        setPaymentAccount(deal.shY_Payment_Account);
    };

    const handleCancelEdit = () => {
        resetDealFieldsFromProps();
        setIsEditMode(false);
    };

    const handleSaveEdit = () => {
        updateDeal();
        setIsEditMode(false);
    };

    const toggleLeg = (legId) => {
        setExpandedLegs((prev) => ({
            ...prev,
            [legId]: !prev[legId],
        }));
    };

    return (
        deal && (
            <>
                <div className="portal_finance_layout">
                    <div className="deal_details_form">
                        <div>
                            <div className="formRow">
                                <label>Requested Date:</label>{" "}
                                {deal.date_Opened ? format(new Date(deal.date_Opened), "dd/MM/yy") : "-"}
                            </div>
                            <div className="formRow">
                                <label>Service:</label> {deal.service}
                            </div>
                            <div className="formRow">
                                <label>Trip Type:</label> {getTripTypeLabelFromLegCount(legs?.length) || "-"}
                            </div>
                            <div className="formRow">
                                <label>Preferred Currency:</label> {deal.preferred_Currency || "-"}
                            </div>
                            <div className="formRow">
                                <label>Invoice Name:</label>{" "}
                                {isEditMode ? (
                                    <TextInput
                                        defaultValue={invoiceName}
                                        label=""
                                        value={(fromValue) => setInvoiceName(fromValue)}
                                    />
                                ) : (
                                    invoiceName || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>SHY Payment Account:</label>{" "}
                                {isEditMode ? (
                                    <SelectInput
                                        options={paymentAccountList}
                                        value={(value) => setPaymentAccount(value)}
                                        defaultValue={paymentAccount}
                                        label=""
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    paymentAccount || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>SHYF Number:</label> {deal.shyF_No}
                            </div>
                            <div className="formRow">
                                <label>Sale Currency:</label>{" "}
                                {isEditMode ? (
                                    <SelectInput
                                        options={currencyList}
                                        value={(value) => setSaleCurrency(value)}
                                        defaultValue={saleCurrency}
                                        label=""
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    saleCurrency || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Sale Price:</label>{" "}
                                {isEditMode ? (
                                    <TextInput
                                        label=""
                                        type="numeric"
                                        defaultValue={salePrice}
                                        value={(newValue) => setSalePrice(newValue)}
                                    />
                                ) : (
                                    formatPriceForDisplay(salePrice) || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Cost Currency:</label>{" "}
                                {isEditMode ? (
                                    <SelectInput
                                        options={currencyList}
                                        value={(value) => setCostCurrency(value)}
                                        defaultValue={costCurrency}
                                        label=""
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    costCurrency || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Cost Price:</label>{" "}
                                {isEditMode ? (
                                    <TextInput
                                        label=""
                                        type="numeric"
                                        defaultValue={costPrice}
                                        value={(newValue) => setCostPrice(newValue)}
                                    />
                                ) : (
                                    formatPriceForDisplay(costPrice) || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Daily FX Margin:</label> {formatGbpForDisplay(deal.daily_FX_Margin) || "-"}
                            </div>
                            <div className="formRow">
                                <label>Operator Chipped Value:</label>{" "}
                                {isEditMode ? (
                                    <TextInput
                                        label=""
                                        type="numeric"
                                        defaultValue={operatorChipping}
                                        value={(newValue) => setOperatorChipping(newValue)}
                                    />
                                ) : (
                                    formatPriceForDisplay(operatorChipping) || "-"
                                )}
                            </div>
                        </div>
                    </div>

                    {legs && legs.length > 0 && (
                        <div className="deal_details_form">
                            <div className="portal-details__legs">
                                <PortalDealDetailsEditToolbar
                                    isEditMode={isEditMode}
                                    onEdit={() => setIsEditMode(true)}
                                    onSave={handleSaveEdit}
                                    onCancel={handleCancelEdit}
                                    sending={sending}
                                />
                                {legs
                                    .sort((a, b) => {
                                        return a.leg_Number < b.leg_Number ? -1 : 1;
                                    })
                                    .map((leg, index) => {
                                        const isExpanded = expandedLegs[leg.id];
                                        const legDate = new Date(leg.leg_Date_and_Time);
                                        return (
                                            <div key={leg.id} className="leg-section">
                                                <div
                                                    className="leg-header-clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-expanded={isExpanded}
                                                    onClick={() => toggleLeg(leg.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            toggleLeg(leg.id);
                                                        }
                                                    }}
                                                >
                                                    <strong>
                                                        Leg {index + 1} - {leg.airport_From} - {leg.airport_To}
                                                    </strong>
                                                    <img
                                                        src={chevronIcon}
                                                        className={
                                                            isExpanded ? "leg-header-clickable__chevron--open" : ""
                                                        }
                                                        alt=""
                                                    />
                                                </div>
                                                {isExpanded && (
                                                    <div className="leg-details">
                                                        <div className="formRow">
                                                            <label>From:</label> {leg.airport_From}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>To:</label> {leg.airport_To}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Departure Date:</label> {format(legDate, "dd/MM/yy")}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Departure Time:</label> {format(legDate, "HH:mm")}
                                                        </div>
                                                        {deal.leg_1_Date_of_Arrival && index === 0 && (
                                                            <>
                                                                <div className="formRow">
                                                                    <label>Arrival Date:</label>{" "}
                                                                    {format(
                                                                        new Date(deal.leg_1_Date_of_Arrival),
                                                                        "dd/MM/yy"
                                                                    )}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Arrival Time:</label>{" "}
                                                                    {deal.leg_1_Time_of_Arrival || "-"}
                                                                </div>
                                                            </>
                                                        )}
                                                        {deal.leg_2_Date_of_Arrival && index === 1 && (
                                                            <>
                                                                <div className="formRow">
                                                                    <label>Arrival Date:</label>{" "}
                                                                    {format(
                                                                        new Date(deal.leg_2_Date_of_Arrival),
                                                                        "dd/MM/yy"
                                                                    )}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Arrival Time:</label>{" "}
                                                                    {deal.leg_2_Time_of_Arrival || "-"}
                                                                </div>
                                                            </>
                                                        )}
                                                        <div className="formRow">
                                                            <label>Pax:</label> {leg.number_of_Passengers}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Bags:</label> {leg.number_of_Bags}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Carry On:</label> {leg.number_of_Carry_On_Bags}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                </div>

                {deal && <PortalFinanceStageBasedCtas deal={deal} />}
                {sending && <LoadingOverlay />}
            </>
        )
    );
};

export default PortalFinanceDetails;
